import { WorkEmpty } from '../work-page.tsx';
import './payment-surfaces.css';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { paymentApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Button, LoadingBlock, Pagination } from '../ui.tsx';
import { PdfDownloadButton } from '../pdf-download.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import type { Customer } from '@shared/types/index.ts';
import { formatPaymentDay } from '../../i18n/datetime.ts';

const HISTORY_PAGE_SIZE = 20;

/**
 * The line on a customer's page that says what they still owe on accepted
 * quotes — on every plan, and silent when they owe nothing.
 */
export function CustomerOwedLine({ customer }: { customer: Customer }) {
  const { t } = useTranslation();
  const { can } = useSession();
  const owed = useQuery({
    queryKey: ['owed', 'customer', customer.id],
    queryFn: () => paymentApi.owed(customer.id),
    enabled: can('payments:read'),
  });
  const data = owed.data;
  if (!data || data.count === 0) return null;
  const money = (cents: number) => formatCents(cents, data.currencyCode, getDateLocale());
  return (
    <p className="mb-4 text-sm" data-testid="customer-owed-line">
      <span className="font-semibold">{t('customerPayments.owed', { amount: money(data.totalCents) })}</span>{' '}
      <span className="text-[color:var(--color-muted)]">
        {data.quotes.map((quote, index) => (
          <span key={quote.quoteId}>
            {index > 0 ? ', ' : ''}
            <Link to={`/quotes/${quote.quoteId}`} className="text-[color:var(--color-brand)] hover:underline">
              {quote.quoteNumber}
            </Link>
          </span>
        ))}
      </span>
    </p>
  );
}

/**
 * The customer's payment history on a plan without invoicing: the payments
 * taken on their quotes (the server lists nothing else there), with each
 * one's receipt. Recording happens on the quote, where the money belongs.
 */
export function CustomerQuotePaymentHistory({ customer }: { customer: Customer }) {
  // Keyed by customer: the route reuses this component from one customer to
  // the next, and the page, notice and error belong to the one they were for.
  return <QuotePaymentHistory key={customer.id} customer={customer} />;
}

function QuotePaymentHistory({ customer }: { customer: Customer }) {
  const { t } = useTranslation();
  const { can } = useSession();
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const history = useQuery({
    queryKey: ['customer-payments', customer.id, page],
    queryFn: () => paymentApi.customerPayments(customer.id, { page, pageSize: HISTORY_PAGE_SIZE }),
    enabled: can('payments:read'),
  });
  const resendReceipt = useMutation({
    mutationFn: (paymentId: string) => {
      setResendingId(paymentId);
      return paymentApi.resendReceipt(paymentId);
    },
    onSuccess: (result) => {
      setResendingId(null);
      setError(null);
      setNotice(t('customerPayments.receiptResent', { email: result.resentTo }));
    },
    onError: (caught: unknown) => {
      setResendingId(null);
      setError(caught instanceof ApiRequestError ? caught.message : t('customerPayments.actionFailed'));
    },
  });

  if (!can('payments:read')) return null;
  const items = history.data?.items ?? [];

  return (
    <section className="card work-section payment-leaf" aria-labelledby="customer-payments-heading">
      <div className="work-section-heading">
        <h2 id="customer-payments-heading" className="mb-1 text-lg font-semibold">
          {t('customerPayments.heading')}
        </h2>
      </div>
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('customerPayments.explainer')}</p>
      {error ? (
        <div className="mb-3">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}
      {notice ? (
        <div className="mb-3">
          <Alert tone="success">{notice}</Alert>
        </div>
      ) : null}
      {history.isLoading ? <LoadingBlock /> : null}
      {history.isError ? <Alert tone="error">{(history.error as Error).message}</Alert> : null}
      {items.length > 0 ? (
        <ul className="divide-y divide-[color:var(--color-line)] text-sm">
          {items.map((payment) => (
            <li key={payment.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span>
                <span className="tabular font-medium">
                  {payment.type === 'refund' ? '−' : ''}
                  {formatCents(payment.amountCents, payment.currencyCode, getDateLocale())}
                </span>
                <span className="ml-2 text-[color:var(--color-muted)]">
                  {payment.type === 'refund'
                    ? t('customerPayments.refundLabel')
                    : (payment.manualMethodName ?? t('customerPayments.recordedLabel'))}
                  {` · ${formatPaymentDay(payment)}`}
                  {payment.description ? ` · ${payment.description}` : ''}
                </span>
                {payment.quoteId && !payment.quoteDeleted ? (
                  <Link
                    to={`/quotes/${payment.quoteId}`}
                    className="ml-2 text-[color:var(--color-brand)] hover:underline"
                  >
                    {t('customerPayments.openQuote')}
                  </Link>
                ) : null}
              </span>
              {payment.type === 'charge' && payment.status === 'succeeded' ? (
                <span className="flex flex-wrap items-center gap-2">
                  <PdfDownloadButton url={paymentApi.receiptUrl(payment.id)} variant="link">
                    {t('customerPayments.downloadReceipt')}
                  </PdfDownloadButton>
                  {can('payments:write') ? (
                    <Button
                      variant="ghost"
                      busy={resendingId === payment.id}
                      onClick={() => resendReceipt.mutate(payment.id)}
                    >
                      {t('customerPayments.resendReceipt')}
                    </Button>
                  ) : null}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : history.data && !history.isError ? (
        <WorkEmpty title={t('customerPayments.empty')} />
      ) : null}
      <Pagination page={page} totalPages={history.data?.totalPages} onPageChange={setPage} />
    </section>
  );
}
